import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import {
  Users,
  Plus,
  Mail,
  Shield,
  User,
  Edit,
  Trash2,
  MoreVertical,
  Lock,
  Check,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

const roles = [
  { id: 'owner', name: 'Owner', description: 'Full access to all features' },
  { id: 'admin', name: 'Admin', description: 'Can manage most features' },
  { id: 'editor', name: 'Editor', description: 'Can edit content' },
  { id: 'viewer', name: 'Viewer', description: 'View-only access' }
];

const sampleMembers = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john@example.com',
    role: 'admin',
    avatar: 'https://ui-avatars.com/api/?name=John+Doe&background=random',
    status: 'active',
    joinedAt: '2024-01-15'
  },
  {
    id: '2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    role: 'editor',
    avatar: 'https://ui-avatars.com/api/?name=Jane+Smith&background=random',
    status: 'active',
    joinedAt: '2024-02-20'
  }
];

const Team = () => {
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const [members, setMembers] = useState(sampleMembers);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [showActionsFor, setShowActionsFor] = useState(null);

  const hasTeamFeature = hasFeature('team_collaboration');

  const handleInvite = () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    const newMember = {
      id: Date.now().toString(),
      name: inviteEmail.split('@')[0],
      email: inviteEmail,
      role: inviteRole,
      avatar: `https://ui-avatars.com/api/?name=${inviteEmail}&background=random`,
      status: 'pending',
      joinedAt: new Date().toISOString()
    };

    setMembers([...members, newMember]);
    toast.success('Invitation sent successfully!');
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('editor');
  };

  const handleRemoveMember = (id) => {
    setMembers(members.filter(m => m.id !== id));
    toast.success('Member removed');
    setShowActionsFor(null);
  };

  const handleChangeRole = (id, newRole) => {
    setMembers(members.map(m => m.id === id ? { ...m, role: newRole } : m));
    toast.success('Role updated');
    setShowActionsFor(null);
  };

  if (!hasTeamFeature) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Collaboration Locked</h2>
        <p className="text-gray-500 text-center max-w-md mb-6">
          Team collaboration is available on Enterprise plan. Upgrade to invite team members.
        </p>
        <button
          onClick={() => window.location.href = '/plans'}
          className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
        >
          Upgrade Plan
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-500">Manage team members and their permissions</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          Invite Member
        </button>
      </div>

      {/* Members List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Team Members ({members.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {/* Current User (Owner) */}
          <div className="p-6 flex items-center justify-between bg-primary-50/50">
            <div className="flex items-center gap-4">
              <img
                src={user?.photoURL || `https://ui-avatars.com/api/?name=${user?.displayName}&background=random`}
                alt={user?.displayName}
                className="w-12 h-12 rounded-full"
              />
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900">{user?.displayName || 'You'}</p>
                  <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                    Owner
                  </span>
                </div>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Other Members */}
          {members.map((member) => (
            <div key={member.id} className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{member.name}</p>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      member.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {member.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{member.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg capitalize">
                  {member.role}
                </span>

                <div className="relative">
                  <button
                    onClick={() => setShowActionsFor(showActionsFor === member.id ? null : member.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>

                  {showActionsFor === member.id && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                      <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                        Change Role
                      </div>
                      {roles.filter(r => r.id !== 'owner').map((role) => (
                        <button
                          key={role.id}
                          onClick={() => handleChangeRole(member.id, role.id)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            member.role === role.id ? 'text-primary-600 bg-primary-50' : 'text-gray-700'
                          }`}
                        >
                          {member.role === role.id && <Check className="w-4 h-4 inline mr-2" />}
                          {role.name}
                        </button>
                      ))}
                      <hr className="my-1" />
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 inline mr-2" />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Roles Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Permissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {roles.map((role) => (
            <div key={role.id} className="p-4 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-900 mb-1">{role.name}</h4>
              <p className="text-sm text-gray-500">{role.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Invite Team Member
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="colleague@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {roles.filter(r => r.id !== 'owner').map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name} - {role.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
              >
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
